import { Moon, Palette, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { PRESETS, type ThemePresetId } from '@/theme/presets';
import {
  applyCustomRamp,
  applyPreset,
  getMode,
  getPreset,
  rampFromHex,
  setMode,
  type ThemeMode,
} from '@/theme/themeRuntime';

/**
 * Full-page theme picker — same engine as the topbar dropdown but with
 * room to breathe. Live preview swatches show how the brand-500 reads
 * against your surface, plus a custom HEX input for the very specific
 * "I want THIS shade of magenta" moments.
 */
export function ThemePage() {
  const [mode, setModeState] = useState<ThemeMode>(getMode());
  const [active, setActive] = useState<ThemePresetId>(getPreset());
  const [hex, setHex] = useState('');

  useEffect(() => {
    setModeState(getMode());
    setActive(getPreset());
  }, []);

  const onPick = (id: ThemePresetId) => {
    applyPreset(id);
    setActive(id);
    toast.success(`${id} applied`);
  };

  const onCustom = () => {
    const ramp = rampFromHex(hex);
    if (!ramp) {
      toast.error('Enter a valid hex like #10B981');
      return;
    }
    applyCustomRamp(ramp);
    toast.success('Custom palette applied');
  };

  const toggleMode = () => {
    const next: ThemeMode = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    setModeState(next);
  };

  return (
    <>
      <PageHeader
        back
        title="Theme & appearance"
        description="Brand colour and dark mode — synced per-device."
      />

      {/* Mode toggle */}
      <Card className="mb-4">
        <SectionHeader
          title="Mode"
          description={
            mode === 'light'
              ? 'Currently using light mode. Easy on the eye in shop-floor lighting.'
              : 'Currently using dark mode. Less battery, easier at night.'
          }
        />
        <Button
          variant="outline"
          iconLeft={mode === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          onClick={toggleMode}
        >
          Switch to {mode === 'light' ? 'dark' : 'light'} mode
        </Button>
      </Card>

      {/* Presets */}
      <Card className="mb-4">
        <SectionHeader title="Brand colour" description="Same eight presets as the mobile app." />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PRESETS.map((p) => {
            const isActive = p.id === active;
            const swatch = `rgb(${p.ramp[500]})`;
            const soft = `rgb(${p.ramp[100]})`;
            return (
              <button
                key={p.id}
                onClick={() => onPick(p.id)}
                className={
                  'rounded-2xl border-2 p-4 text-left transition-all ' +
                  (isActive
                    ? 'border-ink ring-2 ring-brand-500/30'
                    : 'border-line hover:border-ink-soft')
                }
                style={{ background: soft }}
              >
                <div
                  className="h-10 w-10 rounded-xl mb-3"
                  style={{ background: swatch }}
                />
                <div className="text-sm font-semibold text-ink">{p.label}</div>
                <div className="text-xs text-ink-soft">
                  {isActive ? 'Active' : 'Tap to apply'}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Custom HEX */}
      <Card>
        <SectionHeader
          title="Custom HEX"
          description="Pick any colour. We'll generate a 10-stop ramp from it."
        />
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <Input
            name="hex"
            placeholder="#10B981"
            value={hex}
            onChange={(e) => setHex(e.target.value)}
            iconLeft={<Palette className="h-4 w-4" />}
          />
          <Button onClick={onCustom} disabled={!rampFromHex(hex)}>
            Apply custom
          </Button>
        </div>
      </Card>
    </>
  );
}
